import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon, Popup } from "semantic-ui-react";

import { ReactTable } from "components";
import "modules/DealerReports/components/AppointmentSurveyReport/AppointmentSurveyReport.scss";
import { PATHS } from "router/paths";

type SurveyRow = {
  location_name: string;
  wo_nr: string;
  url: string;
  appointment_id: number;
  customer: string;
  star_rating_experience: number;
  star_rating_service: number;
  star_rating_speed: number;
  star_rating_feedback: string;
};

export type SurveyReportData = {
  rows: SurveyRow[];
};

type AppointmentSurveyReportProps = {
  data: SurveyReportData;
};

const columnHelper = createColumnHelper<SurveyRow>();

export const AppointmentSurveyReport = ({ data }: AppointmentSurveyReportProps) => {
  const { t } = useTranslation();

  const columns = [
    columnHelper.accessor(row => row.location_name, {
      id: "location",
      header: t("v8_location", "Location"),
      cell: ({
        row: {
          original: { location_name }
        }
      }) => <span>{location_name}</span>,
      size: 120
    }),
    columnHelper.accessor(row => row.wo_nr, {
      id: "wo_nr",
      header: t("v8_wo", "WO #"),
      cell: ({
        row: {
          original: { wo_nr, appointment_id }
        }
      }) => (
        <Link to={`${PATHS.APPOINTMENTS}/${appointment_id}`} target="_blank">
          <Icon className="arrow up right from square" />
          {wo_nr}
        </Link>
      ),
      size: 100
    }),
    columnHelper.accessor(row => row.customer, {
      id: "customer",
      header: t("v8_customer", "Customer"),
      cell: ({
        row: {
          original: { customer }
        }
      }) => <span>{customer}</span>,
      size: 150
    }),
    columnHelper.accessor(row => row.star_rating_experience, {
      id: "star_rating_experience",
      header: t("v8_experience", "Experience"),
      cell: ({
        row: {
          original: { star_rating_experience }
        }
      }) => <span>{star_rating_experience}/5</span>,
      size: 80
    }),
    columnHelper.accessor(row => row.star_rating_service, {
      id: "star_rating_service",
      header: t("v8_service", "Service"),
      cell: ({
        row: {
          original: { star_rating_service }
        }
      }) => <span>{star_rating_service}/5</span>,
      size: 80
    }),
    columnHelper.accessor(row => row.star_rating_speed, {
      id: "star_rating_speed",
      header: t("v8_speed", "Speed"),
      cell: ({
        row: {
          original: { star_rating_speed }
        }
      }) => <span>{star_rating_speed}/5</span>,
      size: 80
    }),
    columnHelper.accessor(row => row.star_rating_feedback, {
      id: "star_rating_feedback",
      header: t("v8_feedback", "Feedback"),
      cell: ({
        row: {
          original: { star_rating_feedback }
        }
      }) => {
        if (!star_rating_feedback) return null;
        return <Popup content={star_rating_feedback} trigger={<span className="feedback-cell">{star_rating_feedback}</span>} />;
      },
      size: 250
    })
  ];

  return (
    <div className="AppointmentSurveyReport">
      <div className="table-container">
        <ReactTable columns={columns} data={data.rows} />
      </div>
    </div>
  );
};
